import { LAST_WRECK_CONTENT, type RoomId, type SalvageId, type WreckContent } from '../content';
import type {
  ConnectionRuntimeState,
  CutRuntimeState,
  GameState,
  RoomRuntimeState,
  SalvageRuntimeState,
} from './types';

export function normalizeSeed(seed: number): number {
  if (!Number.isFinite(seed)) return 0x1a57b00b;
  const normalized = Math.trunc(seed) >>> 0;
  return normalized === 0 ? 0x1a57b00b : normalized;
}

export function nextRandom(randomState: number): {
  readonly value: number;
  readonly state: number;
} {
  let next = randomState >>> 0;
  next ^= next << 13;
  next ^= next >>> 17;
  next ^= next << 5;
  const state = next >>> 0;
  return { value: state / 0x1_0000_0000, state };
}

export function createInitialGameState(
  seed = 0x1a57b00b,
  content: WreckContent = LAST_WRECK_CONTENT,
): GameState {
  const rooms = Object.fromEntries(
    content.rooms.map((room) => [
      room.id,
      {
        pressure: room.pressure,
        revealedUntil: room.tutorial ? 8 : 0,
        powerVisible: room.tutorial,
      } satisfies RoomRuntimeState,
    ]),
  ) as Record<RoomId, RoomRuntimeState>;
  const connections = Object.fromEntries(
    content.connections.map((connection) => [
      connection.id,
      {
        open: connection.initiallyOpen,
        narrowed: false,
        collapsed: false,
        revealedUntil: connection.id === 'tutorial-hatch' ? 8 : 0,
      } satisfies ConnectionRuntimeState,
    ]),
  ) as Record<string, ConnectionRuntimeState>;
  const cuts = Object.fromEntries(
    content.cutPoints.map((point) => [
      point.id,
      {
        progress: 0,
        heat: 0,
        complete: false,
        revealedUntil: 0,
      } satisfies CutRuntimeState,
    ]),
  ) as Record<string, CutRuntimeState>;
  const salvage = Object.fromEntries(
    content.salvage.map((item) => [
      item.id,
      {
        roomId: item.roomId,
        condition: 100,
        revealedUntil: item.id === 'tool-cassette' ? 8 : 0,
        banked: false,
        attachmentIdsCut: [],
      } satisfies SalvageRuntimeState,
    ]),
  ) as unknown as Record<SalvageId, SalvageRuntimeState>;
  const normalizedSeed = normalizeSeed(seed);

  return {
    version: 1,
    seed: normalizedSeed,
    randomState: normalizedSeed,
    phase: 'tutorial',
    elapsedSeconds: 0,
    currentRoomId: content.extractionRoomId,
    selectedTool: 'scanner',
    scannerCooldownSeconds: 0,
    toolDisruptionSeconds: 0,
    tutorialStep: 'scan-hatch',
    rooms,
    connections,
    cuts,
    salvage,
    power: {
      buses: { A: 'off', B: 'off' },
      hasPortableJumper: false,
      jumper: null,
    },
    grapple: null,
    instability: { value: 10, triggered: [], emergencyRemainingSeconds: null },
    stats: {
      scans: 0,
      cuts: 0,
      grappleAttachments: 0,
      cableBreaks: 0,
      hardImpacts: 0,
      roomsVisited: [content.extractionRoomId],
      targetFirstLocatedAt: null,
      targetExtractedAt: null,
    },
    result: null,
  };
}
